import { memo, useState } from "react"
import { BsThreeDotsVertical } from "react-icons/bs"
import { FaEdit } from "react-icons/fa"
import { FiTrash2 } from "react-icons/fi"

import { Field } from "@/components/atoms/Field"
import { toaster } from "@/components/infrastructure"
import {
  useDeleteHousehold,
  useUpdateHousehold,
} from "@/hooks/api/useHouseholds"
import type { HouseholdResponse, HouseholdUpdateInput } from "@/types/household"
import { householdUpdateSchema } from "@/types/household"
import {
  Box,
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  IconButton,
  Input,
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuTrigger,
  Switch,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Controller,
  type Resolver,
  type SubmitHandler,
  useForm,
} from "react-hook-form"

interface HouseholdActionsMenuProps {
  household: HouseholdResponse
}

export const HouseholdActionsMenu = memo(
  ({ household }: HouseholdActionsMenuProps) => {
    const [showEditDialog, setShowEditDialog] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)

    const deleteHouseholdMutation = useDeleteHousehold()
    const updateHouseholdMutation = useUpdateHousehold()

    const form = useForm<HouseholdUpdateInput>({
      resolver: zodResolver(
        householdUpdateSchema,
      ) as Resolver<HouseholdUpdateInput>,
      mode: "onBlur",
      defaultValues: {
        user_id: household.user_id ?? "",
        parent_first_name: household.parent_first_name ?? "",
        parent_last_name: household.parent_last_name ?? "",
        email: household.email ?? "",
        phone: household.phone ?? "",
        secondary_phone: household.secondary_phone ?? "",
        address: household.address ?? "",
        emergency_contact_name: household.emergency_contact_name ?? "",
        emergency_contact_phone: household.emergency_contact_phone ?? "",
        emergency_contact_relationship:
          household.emergency_contact_relationship ?? "",
        doctor_first_name: household.doctor_first_name ?? "",
        doctor_last_name: household.doctor_last_name ?? "",
        doctor_phone: household.doctor_phone ?? "",
        insurance_provider: household.insurance_provider ?? "",
        referral_source: household.referral_source ?? "",
        agreed_to_policy: household.agreed_to_policy ?? false,
        agreed_to_hold_harmless: household.agreed_to_hold_harmless ?? false,
        policy_agreement_date: household.policy_agreement_date ?? "",
        billing_email: household.billing_email ?? "",
        auto_pay_enabled: household.auto_pay_enabled ?? false,
        notes: household.notes ?? "",
        is_active: household.is_active ?? false,
      },
    })

    const getErrorText = (error: unknown): string | undefined => {
      if (typeof error === "object" && error !== null) {
        const maybeMessage = (error as { message?: unknown }).message
        if (typeof maybeMessage === "string") {
          return maybeMessage
        }
      }
      return undefined
    }

    const displayLabel = String(household.user_id ?? household.id)

    const handleDelete = async () => {
      try {
        await deleteHouseholdMutation.mutateAsync(household.id)
        setShowDeleteDialog(false)
        toaster.create({
          title: "Household deleted",
          description: `${displayLabel} has been deleted successfully`,
          type: "success",
        })
      } catch (error: any) {
        console.error("Error deleting household:", error)

        // Show user-friendly error message
        let errorMessage = "Failed to delete household"
        if (
          error?.message?.includes("Insufficient permissions") ||
          error?.status === 403
        ) {
          errorMessage = "You don't have permission to delete this household"
        } else if (error?.message) {
          errorMessage = error.message
        }

        toaster.create({
          title: "Delete failed",
          description: errorMessage,
          type: "error",
        })
      }
    }

    const handleUpdate: SubmitHandler<HouseholdUpdateInput> = async (data) => {
      try {
        await updateHouseholdMutation.mutateAsync({
          householdId: household.id,
          householdData: data,
        })
        setShowEditDialog(false)
        form.reset({
          user_id: household.user_id ?? "",
          parent_first_name: household.parent_first_name ?? "",
          parent_last_name: household.parent_last_name ?? "",
          email: household.email ?? "",
          phone: household.phone ?? "",
          secondary_phone: household.secondary_phone ?? "",
          address: household.address ?? "",
          emergency_contact_name: household.emergency_contact_name ?? "",
          emergency_contact_phone: household.emergency_contact_phone ?? "",
          emergency_contact_relationship:
            household.emergency_contact_relationship ?? "",
          doctor_first_name: household.doctor_first_name ?? "",
          doctor_last_name: household.doctor_last_name ?? "",
          doctor_phone: household.doctor_phone ?? "",
          insurance_provider: household.insurance_provider ?? "",
          referral_source: household.referral_source ?? "",
          agreed_to_policy: household.agreed_to_policy ?? false,
          agreed_to_hold_harmless: household.agreed_to_hold_harmless ?? false,
          policy_agreement_date: household.policy_agreement_date ?? "",
          billing_email: household.billing_email ?? "",
          auto_pay_enabled: household.auto_pay_enabled ?? false,
          notes: household.notes ?? "",
          is_active: household.is_active ?? false,
        })
        toaster.create({
          title: "Household updated",
          description: `${displayLabel} has been updated successfully`,
          type: "success",
        })
      } catch (error: any) {
        console.error("Error updating household:", error)

        // Show user-friendly error message
        let errorMessage = "Failed to update household"
        if (
          error?.message?.includes("Insufficient permissions") ||
          error?.status === 403
        ) {
          errorMessage = "You don't have permission to edit this household"
        } else if (error?.message) {
          errorMessage = error.message
        }

        toaster.create({
          title: "Update failed",
          description: errorMessage,
          type: "error",
        })
      }
    }

    const handleEditClose = () => {
      setShowEditDialog(false)
      form.reset({
        user_id: household.user_id ?? "",
        parent_first_name: household.parent_first_name ?? "",
        parent_last_name: household.parent_last_name ?? "",
        email: household.email ?? "",
        phone: household.phone ?? "",
        secondary_phone: household.secondary_phone ?? "",
        address: household.address ?? "",
        emergency_contact_name: household.emergency_contact_name ?? "",
        emergency_contact_phone: household.emergency_contact_phone ?? "",
        emergency_contact_relationship:
          household.emergency_contact_relationship ?? "",
        doctor_first_name: household.doctor_first_name ?? "",
        doctor_last_name: household.doctor_last_name ?? "",
        doctor_phone: household.doctor_phone ?? "",
        insurance_provider: household.insurance_provider ?? "",
        referral_source: household.referral_source ?? "",
        agreed_to_policy: household.agreed_to_policy ?? false,
        agreed_to_hold_harmless: household.agreed_to_hold_harmless ?? false,
        policy_agreement_date: household.policy_agreement_date ?? "",
        billing_email: household.billing_email ?? "",
        auto_pay_enabled: household.auto_pay_enabled ?? false,
        notes: household.notes ?? "",
        is_active: household.is_active ?? false,
      })
    }

    return (
      <>
        <MenuRoot>
          <MenuTrigger asChild>
            <IconButton
              variant="ghost"
              color="inherit"
              aria-label={`Actions for ${displayLabel}`}
            >
              <BsThreeDotsVertical />
            </IconButton>
          </MenuTrigger>
          <MenuContent>
            <MenuItem value="edit" onClick={() => setShowEditDialog(true)}>
              <FaEdit fontSize="14px" aria-hidden="true" />
              Edit
            </MenuItem>
            <MenuItem value="delete" onClick={() => setShowDeleteDialog(true)}>
              <FiTrash2 fontSize="14px" aria-hidden="true" />
              Delete
            </MenuItem>
          </MenuContent>
        </MenuRoot>

        {/* Edit Dialog */}
        <DialogRoot
          open={showEditDialog}
          onOpenChange={(e) => !e.open && handleEditClose()}
          size="md"
        >
          <DialogBackdrop />
          <DialogContent as="form" onSubmit={form.handleSubmit(handleUpdate)}>
            <DialogHeader>
              <DialogTitle>Edit Household</DialogTitle>
              <DialogCloseTrigger />
            </DialogHeader>

            <DialogBody>
              <VStack gap={6}>
                <Field
                  invalid={!!form.formState.errors.user_id}
                  errorText={getErrorText(form.formState.errors.user_id)}
                  label="User Id"
                >
                  <Input
                    placeholder={"Enter user id"}
                    aria-invalid={!!form.formState.errors.user_id}
                    {...form.register("user_id")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.parent_first_name}
                  errorText={getErrorText(
                    form.formState.errors.parent_first_name,
                  )}
                  label="Parent First Name"
                >
                  <Input
                    placeholder={"Enter parent first name"}
                    aria-invalid={!!form.formState.errors.parent_first_name}
                    {...form.register("parent_first_name")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.parent_last_name}
                  errorText={getErrorText(
                    form.formState.errors.parent_last_name,
                  )}
                  label="Parent Last Name"
                >
                  <Input
                    placeholder={"Enter parent last name"}
                    aria-invalid={!!form.formState.errors.parent_last_name}
                    {...form.register("parent_last_name")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.email}
                  errorText={getErrorText(form.formState.errors.email)}
                  label="Email"
                >
                  <Input
                    placeholder={"Enter email"}
                    aria-invalid={!!form.formState.errors.email}
                    {...form.register("email")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.phone}
                  errorText={getErrorText(form.formState.errors.phone)}
                  label="Phone"
                >
                  <Input
                    placeholder={"Enter phone"}
                    aria-invalid={!!form.formState.errors.phone}
                    {...form.register("phone")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.secondary_phone}
                  errorText={getErrorText(
                    form.formState.errors.secondary_phone,
                  )}
                  label="Secondary Phone"
                >
                  <Input
                    placeholder={"Enter secondary phone"}
                    aria-invalid={!!form.formState.errors.secondary_phone}
                    {...form.register("secondary_phone")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.address}
                  errorText={getErrorText(form.formState.errors.address)}
                  label="Address"
                >
                  <Textarea
                    {...form.register("address")}
                    placeholder={"Enter address"}
                    rows={3}
                    resize="vertical"
                    aria-invalid={!!form.formState.errors.address}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.emergency_contact_name}
                  errorText={getErrorText(
                    form.formState.errors.emergency_contact_name,
                  )}
                  label="Emergency Contact Name"
                >
                  <Input
                    placeholder={"Enter emergency contact name"}
                    aria-invalid={
                      !!form.formState.errors.emergency_contact_name
                    }
                    {...form.register("emergency_contact_name")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.emergency_contact_phone}
                  errorText={getErrorText(
                    form.formState.errors.emergency_contact_phone,
                  )}
                  label="Emergency Contact Phone"
                >
                  <Input
                    placeholder={"Enter emergency contact phone"}
                    aria-invalid={
                      !!form.formState.errors.emergency_contact_phone
                    }
                    {...form.register("emergency_contact_phone")}
                  />
                </Field>{" "}
                <Field
                  invalid={
                    !!form.formState.errors.emergency_contact_relationship
                  }
                  errorText={getErrorText(
                    form.formState.errors.emergency_contact_relationship,
                  )}
                  label="Emergency Contact Relationship"
                >
                  <Input
                    placeholder={"Enter emergency contact relationship"}
                    aria-invalid={
                      !!form.formState.errors.emergency_contact_relationship
                    }
                    {...form.register("emergency_contact_relationship")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.doctor_first_name}
                  errorText={getErrorText(
                    form.formState.errors.doctor_first_name,
                  )}
                  label="Doctor First Name"
                >
                  <Input
                    placeholder={"Enter doctor first name"}
                    aria-invalid={!!form.formState.errors.doctor_first_name}
                    {...form.register("doctor_first_name")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.doctor_last_name}
                  errorText={getErrorText(
                    form.formState.errors.doctor_last_name,
                  )}
                  label="Doctor Last Name"
                >
                  <Input
                    placeholder={"Enter doctor last name"}
                    aria-invalid={!!form.formState.errors.doctor_last_name}
                    {...form.register("doctor_last_name")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.doctor_phone}
                  errorText={getErrorText(form.formState.errors.doctor_phone)}
                  label="Doctor Phone"
                >
                  <Input
                    placeholder={"Enter doctor phone"}
                    aria-invalid={!!form.formState.errors.doctor_phone}
                    {...form.register("doctor_phone")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.insurance_provider}
                  errorText={getErrorText(
                    form.formState.errors.insurance_provider,
                  )}
                  label="Insurance Provider"
                >
                  <Input
                    placeholder={"Enter insurance provider"}
                    aria-invalid={!!form.formState.errors.insurance_provider}
                    {...form.register("insurance_provider")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.referral_source}
                  errorText={getErrorText(
                    form.formState.errors.referral_source,
                  )}
                  label="Referral Source"
                >
                  <Input
                    placeholder={"Enter referral source"}
                    aria-invalid={!!form.formState.errors.referral_source}
                    {...form.register("referral_source")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.agreed_to_policy}
                  errorText={getErrorText(
                    form.formState.errors.agreed_to_policy,
                  )}
                  label="Agreed To Policy"
                >
                  <Controller
                    name="agreed_to_policy"
                    control={form.control}
                    render={({ field: controllerField }) => (
                      <Switch.Root
                        id="agreed_to_policy"
                        checked={!!controllerField.value}
                        onCheckedChange={({ checked }) =>
                          controllerField.onChange(checked)
                        }
                        aria-label="Agreed To Policy"
                      >
                        <Switch.HiddenInput
                          ref={controllerField.ref}
                          name={controllerField.name}
                          onBlur={controllerField.onBlur}
                        />
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                      </Switch.Root>
                    )}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.agreed_to_hold_harmless}
                  errorText={getErrorText(
                    form.formState.errors.agreed_to_hold_harmless,
                  )}
                  label="Agreed To Hold Harmless"
                >
                  <Controller
                    name="agreed_to_hold_harmless"
                    control={form.control}
                    render={({ field: controllerField }) => (
                      <Switch.Root
                        id="agreed_to_hold_harmless"
                        checked={!!controllerField.value}
                        onCheckedChange={({ checked }) =>
                          controllerField.onChange(checked)
                        }
                        aria-label="Agreed To Hold Harmless"
                      >
                        <Switch.HiddenInput
                          ref={controllerField.ref}
                          name={controllerField.name}
                          onBlur={controllerField.onBlur}
                        />
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                      </Switch.Root>
                    )}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.policy_agreement_date}
                  errorText={getErrorText(
                    form.formState.errors.policy_agreement_date,
                  )}
                  label="Policy Agreement Date"
                >
                  <Input
                    type="datetime-local"
                    placeholder={"Enter policy agreement date"}
                    aria-invalid={!!form.formState.errors.policy_agreement_date}
                    {...form.register("policy_agreement_date")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.billing_email}
                  errorText={getErrorText(form.formState.errors.billing_email)}
                  label="Billing Email"
                >
                  <Input
                    placeholder={"Enter billing email"}
                    aria-invalid={!!form.formState.errors.billing_email}
                    {...form.register("billing_email")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.auto_pay_enabled}
                  errorText={getErrorText(
                    form.formState.errors.auto_pay_enabled,
                  )}
                  label="Auto Pay Enabled"
                >
                  <Controller
                    name="auto_pay_enabled"
                    control={form.control}
                    render={({ field: controllerField }) => (
                      <Switch.Root
                        id="auto_pay_enabled"
                        checked={!!controllerField.value}
                        onCheckedChange={({ checked }) =>
                          controllerField.onChange(checked)
                        }
                        aria-label="Auto Pay Enabled"
                      >
                        <Switch.HiddenInput
                          ref={controllerField.ref}
                          name={controllerField.name}
                          onBlur={controllerField.onBlur}
                        />
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                      </Switch.Root>
                    )}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.notes}
                  errorText={getErrorText(form.formState.errors.notes)}
                  label="Notes"
                >
                  <Textarea
                    {...form.register("notes")}
                    placeholder={"Enter notes"}
                    rows={3}
                    resize="vertical"
                    aria-invalid={!!form.formState.errors.notes}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.is_active}
                  errorText={getErrorText(form.formState.errors.is_active)}
                  label="Is Active"
                >
                  <Controller
                    name="is_active"
                    control={form.control}
                    render={({ field: controllerField }) => (
                      <Switch.Root
                        id="is_active"
                        checked={!!controllerField.value}
                        onCheckedChange={({ checked }) =>
                          controllerField.onChange(checked)
                        }
                        aria-label="Is Active"
                      >
                        <Switch.HiddenInput
                          ref={controllerField.ref}
                          name={controllerField.name}
                          onBlur={controllerField.onBlur}
                        />
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                      </Switch.Root>
                    )}
                  />
                </Field>{" "}
              </VStack>
            </DialogBody>

            <DialogFooter>
              <HStack gap={3}>
                <Button
                  variant="ghost"
                  onClick={handleEditClose}
                  disabled={updateHouseholdMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorPalette="blue"
                  loading={updateHouseholdMutation.isPending}
                  loadingText="Updating..."
                >
                  Update Household
                </Button>
              </HStack>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>

        {/* Delete Dialog */}
        <DialogRoot
          open={showDeleteDialog}
          onOpenChange={(e) => !e.open && setShowDeleteDialog(false)}
          size="md"
        >
          <DialogBackdrop />
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Household</DialogTitle>
              <DialogCloseTrigger />
            </DialogHeader>

            <DialogBody>
              <Box
                bg="yellow.50"
                border="1px solid"
                borderColor="yellow.200"
                borderRadius="md"
                p={4}
                mb={4}
              >
                <Text color="yellow.800">
                  Are you sure you want to delete{" "}
                  <strong>"{displayLabel}"</strong>?
                </Text>
              </Box>

              <Text fontSize="sm" color="gray.600">
                This action cannot be undone. The household will be permanently
                removed from the system.
              </Text>
            </DialogBody>

            <DialogFooter>
              <HStack gap={3}>
                <Button
                  variant="ghost"
                  onClick={() => setShowDeleteDialog(false)}
                  disabled={deleteHouseholdMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  colorPalette="red"
                  onClick={handleDelete}
                  disabled={deleteHouseholdMutation.isPending}
                >
                  {deleteHouseholdMutation.isPending
                    ? "Deleting..."
                    : "Delete Household"}
                </Button>
              </HStack>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>
      </>
    )
  },
)

HouseholdActionsMenu.displayName = "HouseholdActionsMenu"

export default HouseholdActionsMenu
